

import React, { useContext, useState, useMemo } from 'react';
import { NotificationContext } from '../contexts/NotificationContext';

const NotificationsPage = () => {
  const { notifications } = useContext(NotificationContext);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter and search notifications
  const filteredNotifications = useMemo(() => {
    let filtered = notifications || [];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(notif => 
        notif.message?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply time filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const week = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    switch (filter) {
      case 'today':
        filtered = filtered.filter(notif => new Date(notif.date) >= today);
        break;
      case 'week':
        filtered = filtered.filter(notif => new Date(notif.date) >= week);
        break;
      case 'unread':
        filtered = filtered.filter(notif => !notif.read);
        break;
      default:
        break;
    }
    
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [notifications, filter, searchTerm]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'assignment':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
          </svg>
        );
      case 'grade':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"/>
          </svg>
        );
      case 'announcement':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,2A3,3 0 0,1 15,5V11A3,3 0 0,1 12,14A3,3 0 0,1 9,11V5A3,3 0 0,1 12,2M19,11C19,14.53 16.39,17.44 13,17.93V21H11V17.93C7.61,17.44 5,14.53 5,11H7A5,5 0 0,0 12,16A5,5 0 0,0 17,11H19Z"/>
          </svg>
        );
      case 'reminder':
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21,19V20H3V19L5,17V11C5,7.9 7.03,5.17 10,4.29C10,4.19 10,4.1 10,4A2,2 0 0,1 12,2A2,2 0 0,1 14,4C14,4.1 14,4.19 14,4.29C16.97,5.17 19,7.9 19,11V17L21,19M14,21A2,2 0 0,1 12,23A2,2 0 0,1 10,21"/>
          </svg>
        );
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'assignment':
        return 'linear-gradient(45deg, #3b82f6, #1d4ed8)';
      case 'grade':
        return 'linear-gradient(45deg, #10b981, #059669)';
      case 'announcement':
        return 'linear-gradient(45deg, #f59e0b, #d97706)';
      case 'reminder':
        return 'linear-gradient(45deg, #8b5cf6, #7c3aed)';
      default:
        return 'linear-gradient(45deg, #667eea, #764ba2)';
    }
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diff = now - notifDate;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return notifDate.toLocaleDateString();
  };

  const unreadCount = notifications?.filter(n => !n.read).length || 0;

  return (
    <div className="notifications-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .notifications-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .notifications-container::before {
          content: '';
          position: absolute;
          top: 12%;
          right: 10%;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.07);
          animation: float 5s ease-in-out infinite;
        }

        .notifications-container::after {
          content: '';
          position: absolute;
          bottom: 25%;
          left: 5%;
          width: 80px;
          height: 80px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 3.5s ease-in-out infinite reverse;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(10deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes notifSlide {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 0.8; }
        }

        .pulse-element {
          position: absolute;
          top: 40%;
          right: 30%;
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: pulse 4s ease-in-out infinite;
        }

        .notifications-wrapper {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1.2rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }

        .main-title {
          font-size: clamp(2.5rem, 5vw, 3.2rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .notification-badge {
          background: linear-gradient(45deg, #FE6B8B, #FF8E53);
          color: white;
          padding: 0.3rem 0.8rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 600;
          box-shadow: 0 4px 15px rgba(254, 107, 139, 0.4);
          animation: pulse 2s ease-in-out infinite;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1.1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .controls-section {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 2rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 10px 30px rgba(0,0,0,0.1);
          animation: slideIn 0.8s ease-out 0.3s both;
        }

        .controls-grid {
          display: grid;
          grid-template-columns: 1fr auto;
          gap: 1.5rem;
          align-items: center;
        }

        .search-container {
          position: relative;
        }

        .search-input {
          width: 100%;
          padding: 1rem 1rem 1rem 3rem;
          border: 2px solid rgba(102, 126, 234, 0.1);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.8);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          box-sizing: border-box;
        }

        .search-input:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          pointer-events: none;
        }

        .filter-tabs {
          display: flex;
          gap: 0.5rem;
          background: rgba(102, 126, 234, 0.1);
          padding: 0.5rem;
          border-radius: 12px;
        }

        .filter-tab {
          padding: 0.75rem 1.5rem;
          border: none;
          border-radius: 8px;
          background: transparent;
          color: #667eea;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .filter-tab.active {
          background: #667eea;
          color: white;
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
        }

        .filter-tab:hover:not(.active) {
          background: rgba(102, 126, 234, 0.1);
        }

        .notifications-list {
          space-y: 1rem;
        }

        .notification-item {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 1rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 25px rgba(0,0,0,0.08);
          transition: all 0.3s ease;
          animation: notifSlide 0.5s ease-out;
          position: relative;
          overflow: hidden;
        }

        .notification-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 15px 35px rgba(0,0,0,0.12);
        }

        .notification-item.unread {
          border-left: 4px solid #667eea;
        }

        .notification-item.unread::before {
          content: '';
          position: absolute;
          top: 1rem;
          right: 1rem;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #667eea;
          animation: pulse 2s ease-in-out infinite;
        }

        .notification-header {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .notification-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.2);
        }

        .notification-content {
          flex: 1;
          min-width: 0;
        }

        .notification-message {
          font-size: 1rem;
          font-weight: 600;
          color: #1f2937;
          line-height: 1.5;
          margin-bottom: 0.5rem;
          word-wrap: break-word;
        }

        .notification-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .notification-time {
          color: #6b7280;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .notification-type {
          background: rgba(102, 126, 234, 0.1);
          color: #667eea;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          text-transform: capitalize;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border-radius: 20px;
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .empty-icon {
          width: 80px;
          height: 80px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: white;
          font-size: 2rem;
        }

        .empty-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          margin-bottom: 0.5rem;
        }

        .empty-description {
          color: rgba(255, 255, 255, 0.8);
          font-size: 1rem;
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .notifications-container {
            padding: 1rem 0.5rem;
          }
          
          .controls-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .filter-tabs {
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          
          .filter-tabs::-webkit-scrollbar {
            display: none;
          }
          
          .notification-header {
            align-items: center;
          }
          
          .notification-meta {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
          
          .main-title {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>

      <div className="pulse-element"></div>
      
      <div className="notifications-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21,19V20H3V19L5,17V11C5,7.9 7.03,5.17 10,4.29C10,4.19 10,4.1 10,4A2,2 0 0,1 12,2A2,2 0 0,1 14,4C14,4.1 14,4.19 14,4.29C16.97,5.17 19,7.9 19,11V17L21,19M14,21A2,2 0 0,1 12,23A2,2 0 0,1 10,21"/>
            </svg>
            Stay Updated
          </div>
          
          <h1 className="main-title">
            <span className="gradient-text">Notifications</span>
            {unreadCount > 0 && (
              <span className="notification-badge">
                {unreadCount} new
              </span>
            )}
          </h1>
          
          <p className="subtitle">
            Keep track of important updates and announcements
          </p>
        </div>

        {/* Controls Section */}
        <div className="controls-section">
          <div className="controls-grid">
            <div className="search-container">
              <div className="search-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9.5,3A6.5,6.5 0 0,1 16,9.5C16,11.11 15.41,12.59 14.44,13.73L14.71,14H15.5L20.5,19L19,20.5L14,15.5V14.71L13.73,14.44C12.59,15.41 11.11,16 9.5,16A6.5,6.5 0 0,1 3,9.5A6.5,6.5 0 0,1 9.5,3M9.5,5C7,5 5,7 5,9.5C5,12 7,14 9.5,14C12,14 14,12 14,9.5C14,7 12,5 9.5,5Z"/>
                </svg>
              </div>
              <input
                type="text"
                className="search-input"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-tabs">
              <button
                className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button
                className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
                onClick={() => setFilter('unread')}
              >
                Unread
              </button>
              <button
                className={`filter-tab ${filter === 'today' ? 'active' : ''}`}
                onClick={() => setFilter('today')}
              >
                Today
              </button>
              <button
                className={`filter-tab ${filter === 'week' ? 'active' : ''}`}
                onClick={() => setFilter('week')}
              >
                This Week
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              {searchTerm ? '🔍' : '📭'}
            </div>
            <h3 className="empty-title">
              {searchTerm ? 'No Matching Notifications' : 'No Notifications'}
            </h3>
            <p className="empty-description">
              {searchTerm 
                ? `No notifications found matching "${searchTerm}"`
                : filter === 'unread' 
                  ? 'All caught up! No unread notifications.'
                  : 'No notifications available at the moment.'
              }
            </p>
          </div>
        ) : (
          <div className="notifications-list">
            {filteredNotifications.map((notif, index) => (
              <div 
                key={index} 
                className={`notification-item ${!notif.read ? 'unread' : ''}`}
                style={{animationDelay: `${index * 0.1}s`}}
              >
                <div className="notification-header">
                  <div 
                    className="notification-icon"
                    style={{ background: getNotificationColor(notif.type) }}
                  >
                    {getNotificationIcon(notif.type)}
                  </div>
                  
                  <div className="notification-content">
                    <div className="notification-message">
                      {notif.message}
                    </div>
                    
                    <div className="notification-meta">
                      <span className="notification-time">
                        {formatTimeAgo(notif.date)}
                      </span>
                      {notif.type && (
                        <span className="notification-type">
                          {notif.type}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;